import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";

/**
 * URL-based corpus ingest — one-click loader for a JSONL Q&A corpus
 * hosted at any public URL (e.g. an emergentagent.net artifact).
 *
 * POST /api/admin/corpus/ingest-jsonl-url { url, dataset_name, ..., wipe_first }
 * GET  /api/admin/corpus/ingest-jsonl-url/status  → live progress
 *
 * Same UX pattern as AdminCorpusIngestSection but with a URL input +
 * a "wipe existing rows first" checkbox. Handles 200MB+ files because
 * the server streams the download instead of loading it in memory.
 */

const DEFAULT_URL = "";
const DEFAULT_DATASET = "curated_gold_ge9_persona";
const EXPECTED_ROWS = 55000;   // used for the progress bar denominator

export default function AdminCorpusJsonlUrlSection() {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [url, setUrl] = useState(DEFAULT_URL);
  const [datasetName, setDatasetName] = useState(DEFAULT_DATASET);
  const [wipeFirst, setWipeFirst] = useState(true);
  const [minScore, setMinScore] = useState(9.0);
  const pollRef = useRef(null);

  async function fetchStatus() {
    try {
      const r = await fetch(`${API}/admin/corpus/ingest-jsonl-url/status`, {
        credentials: "include",
      });
      if (!r.ok) return null;
      const data = await r.json();
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    fetchStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (status?.state === "running") {
      if (!pollRef.current) {
        pollRef.current = setInterval(fetchStatus, 3000);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [status?.state]);

  async function handleStart() {
    if (starting) return;
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
      toast.error("URL must start with http:// or https://");
      return;
    }
    setStarting(true);
    try {
      const r = await fetch(`${API}/admin/corpus/ingest-jsonl-url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: trimmed,
          dataset_name: datasetName.trim() || DEFAULT_DATASET,
          importance: 0.95,
          min_score: Number(minScore) || 9.0,
          wipe_first: !!wipeFirst,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data?.detail || "Ingest failed to start.");
        return;
      }
      toast.success(
        wipeFirst
          ? "Ingest started. Existing rows will be wiped first, then the new corpus streams in."
          : "Ingest started. Existing rows are preserved; new rows will upsert.",
      );
      setStatus({
        state: "running",
        read: 0, ok: 0,
        skipped_lowscore: 0, skipped_missing: 0, failed: 0,
      });
      fetchStatus();
    } catch (e) {
      toast.error(e?.message || "Network error starting ingest.");
    } finally {
      setStarting(false);
    }
  }

  const s = status || { state: "never_run" };
  const isRunning = s.state === "running";
  const isDone = s.state === "done";
  const isError = s.state === "error";
  const okCount = s.ok || 0;
  const readCount = s.read || 0;
  const skippedLow = s.skipped_lowscore || 0;
  const skippedMissing = s.skipped_missing || 0;
  const failedCount = s.failed || 0;
  const deletedExisting = s.deleted_existing;
  const pct = isRunning
    ? Math.min(99, Math.round((readCount / EXPECTED_ROWS) * 100))
    : isDone ? 100 : 0;

  return (
    <div
      className="border border-[var(--border)] p-5 space-y-4"
      data-testid="corpus-jsonl-url-panel"
    >
      <div className="space-y-1">
        <p className="font-ui text-sm text-[var(--text)] leading-relaxed">
          Loads a <strong>JSONL Q&amp;A corpus</strong> from any public URL
          into <code>hbm_corpus</code>. Server streams the download so 200MB+
          files never blow up memory. Idempotent — same <code>record_id</code>
          upserts cleanly. Recommended for pushing new gold drops
          (50K → 100K → 500K → 1M) into production without a redeploy.
        </p>
        <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-2">
          Runs in background · non-blocking · ~15 min for 50K rows on CPU
        </p>
      </div>

      {/* URL + dataset + options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="md:col-span-3 flex flex-col gap-1.5">
          <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
            JSONL URL
          </span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={isRunning}
            placeholder="https://…/hbm_corpus.jsonl"
            data-testid="corpus-jsonl-url-input"
            className="bg-transparent border border-[var(--border)] px-3 py-2 font-mono-ui text-[12px] text-[var(--text)] focus:border-[var(--accent)] focus:outline-none disabled:opacity-50"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
            Dataset name
          </span>
          <input
            type="text"
            value={datasetName}
            onChange={(e) => setDatasetName(e.target.value)}
            disabled={isRunning}
            data-testid="corpus-jsonl-dataset-input"
            className="bg-transparent border border-[var(--border)] px-3 py-2 font-mono-ui text-[12px] text-[var(--text)] focus:border-[var(--accent)] focus:outline-none disabled:opacity-50"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
            Min score
          </span>
          <input
            type="number"
            step="0.1"
            min="0"
            max="10"
            value={minScore}
            onChange={(e) => setMinScore(e.target.value)}
            disabled={isRunning}
            data-testid="corpus-jsonl-minscore-input"
            className="bg-transparent border border-[var(--border)] px-3 py-2 font-mono-ui text-[12px] text-[var(--text)] focus:border-[var(--accent)] focus:outline-none disabled:opacity-50"
          />
        </label>
        <label className="flex items-center gap-2 md:col-span-1">
          <input
            type="checkbox"
            checked={wipeFirst}
            onChange={(e) => setWipeFirst(e.target.checked)}
            disabled={isRunning}
            data-testid="corpus-jsonl-wipe-checkbox"
            className="accent-[var(--accent)]"
          />
          <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
            Wipe existing rows first
          </span>
        </label>
      </div>

      <div className="flex items-center justify-end">
        <button
          onClick={handleStart}
          disabled={starting || isRunning || !url.trim()}
          data-testid="corpus-jsonl-start"
          className="px-5 py-2.5 bg-[var(--accent)] text-[var(--bg)] hover:opacity-90 font-mono-ui text-[10px] tracking-[0.28em] uppercase transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {starting ? "Starting…" : isRunning ? "Running…" : isDone ? "Re-run" : "Start ingest"}
        </button>
      </div>

      {/* Status row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
        <StatBox label="State" value={s.state || "never_run"} testid="corpus-jsonl-state" />
        <StatBox label="Read" value={readCount.toLocaleString()} testid="corpus-jsonl-read" />
        <StatBox label="Ingested" value={okCount.toLocaleString()} testid="corpus-jsonl-ok" />
        <StatBox label="Skipped" value={(skippedLow + skippedMissing).toLocaleString()} testid="corpus-jsonl-skipped" />
        <StatBox label="Failed" value={failedCount.toLocaleString()} testid="corpus-jsonl-failed" />
      </div>

      {/* Wiped notice */}
      {deletedExisting != null && (
        <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-orange-400/90">
          Wiped {deletedExisting.toLocaleString()} existing rows before ingest.
        </p>
      )}

      {/* Progress bar during run */}
      {(isRunning || isDone) && (
        <div>
          <div className="h-1.5 bg-[var(--border)] overflow-hidden">
            <div
              className="h-full bg-[var(--accent)] transition-all duration-500"
              style={{ width: `${pct}%` }}
              data-testid="corpus-jsonl-progress-bar"
            />
          </div>
          <div className="flex items-center justify-between mt-2 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]">
            <span data-testid="corpus-jsonl-dataset-label">
              {s.dataset_name ? `Dataset: ${s.dataset_name}` : ""}
            </span>
            <span>{pct}%</span>
          </div>
        </div>
      )}

      {isError && s.error && (
        <div
          className="border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-400 font-ui"
          data-testid="corpus-jsonl-error"
        >
          {s.error}
        </div>
      )}

      {s.started_at && (
        <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)]">
          Started {new Date(s.started_at).toLocaleString()}
          {s.started_by ? ` by ${s.started_by}` : ""}
          {s.finished_at && ` · finished ${new Date(s.finished_at).toLocaleString()}`}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="border border-[var(--border)] px-3 py-2">
      <div className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
        {label}
      </div>
      <div className="font-mono-ui text-sm text-[var(--text)] mt-1 break-words" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}
